import type { ReactNode } from "react";

/**
 * `P0-UIK-013`: the first element of 4.6.4's composition , *"every app uses the
 * same shell composition: `<AuthGate>` then `<ShellStoreProvider>` then
 * `<Layout>` then the router outlet."*
 *
 * **Danger sentence.** The tempting shape is to render the screen and let its
 * queries answer `401`. That leaks: the screen mounts, its skeleton shows the
 * shape of data the caller may not have, and its effects fire , a screen that
 * should never have existed for this user has already asked the API three
 * questions. So nothing below the gate mounts until the session is known.
 *
 * And a `pending` session renders neither the screen NOR the sign-in, because
 * flashing a login at an authenticated user reloading a page teaches them their
 * session is unreliable. Three states, three renders, no guessing.
 *
 * A public route (`/depot/:token`, the cabinet tour link) bypasses the gate
 * entirely: 6.3.6 makes the token itself the authorisation, and sending its
 * holder to a login they cannot pass is a dead end.
 */

export type SessionStatus = "pending" | "authenticated" | "anonymous";

export interface AuthGateProps {
  readonly status: SessionStatus;
  /** Rendered while the session is unknown. The app owns the look. */
  readonly pending: ReactNode;
  /** Rendered for an anonymous caller on a private route: a sign-in screen, or a redirect the app performs. */
  readonly signIn: ReactNode;
  /** `true` for 4.6.4's public link routes, whose token is their authorisation. */
  readonly isPublicRoute?: boolean;
  readonly children: ReactNode;
}

export function AuthGate({
  status,
  pending,
  signIn,
  isPublicRoute = false,
  children,
}: AuthGateProps): React.JSX.Element {
  if (isPublicRoute) return <>{children}</>;
  if (status === "pending") return <>{pending}</>;
  if (status === "anonymous") return <>{signIn}</>;
  return <>{children}</>;
}
